import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { has, isArray } from 'lodash';

import api, { openApi } from '../../services/api';
import toastError from '../../errors/toastError';
import { unsubscribeAndUnregister } from '../../services/pushSubscriptionService';
import { socketConnection } from '../../services/socket';

const SPLASH_MIN_MS = 5500; // tempo mínimo da splash (barra chega a 100% em ~0,8s e fica visível)

const useAuth = () => {
  const history = useHistory();
  const splashTimeoutRef = useRef(null);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({});
  const [loginOrigin, setLoginOrigin] = useState(
    () => localStorage.getItem('loginOrigin') || 'default'
  );

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Tenta fazer parse se for string JSON
          const parsedToken = token.startsWith('"') ? JSON.parse(token) : token;
          config.headers['Authorization'] = `Bearer ${parsedToken}`;
          setIsAuth(true);
        } catch (e) {
          // Se falhar, usa o token direto
          config.headers['Authorization'] = `Bearer ${token}`;
          setIsAuth(true);
        }
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Bloqueio comercial: backend retorna 403 com código de motivo.
      // Propaga o erro normalmente — o PlanPermissionsContext e o layout
      // lêem user.company.status para exibir a tela de bloqueio adequada.
      // Não fazemos logout em 403 pois o usuário ainda está autenticado.
      if (error?.response?.status === 403) {
        return Promise.reject(error);
      }

      // Fluxo de refresh conforme doc: em 401 tentar refresh, retry, ou logout
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          doLogout();
          return Promise.reject(error);
        }

        try {
          const { data } = await openApi.post('/auth/refresh_token', { refreshToken });
          if (data?.token && data?.refreshToken) {
            localStorage.setItem('token', data.token);
            localStorage.setItem('refreshToken', data.refreshToken);
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            originalRequest.headers.Authorization = `Bearer ${data.token}`;
            return api(originalRequest);
          }
        } catch (_) {
          // Refresh falhou → logout e redirect
        }

        doLogout();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  const doLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    api.defaults.headers.Authorization = undefined;
    setIsAuth(false);
    setUser({});
    const origin = localStorage.getItem('loginOrigin') || 'default';
    history.push(origin === 'mobile' ? '/mobile-login' : '/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');

    // Timer fixo: esconde a splash só depois de SPLASH_MIN_MS (barra já chegou a 100%)
    splashTimeoutRef.current = setTimeout(() => {
      setLoading(false);
    }, SPLASH_MIN_MS);

    // Verificação do token em paralelo (não controla o loading)
    // Usa /auth/me em vez de refresh_token no carregamento inicial: o token vem do login e está em localStorage.
    // O refresh_token pode retornar 401 (ex.: espera cookie HttpOnly que não está disponível após redirect).
    // O /auth/me valida o Bearer e retorna o user. O refresh_token continua sendo usado no interceptor para 401.
    (async () => {
      if (token) {
        try {
          const { data } = await api.get('/auth/me');
          api.defaults.headers.Authorization = `Bearer ${token}`;
          setIsAuth(true);
          setUser(data);
        } catch (err) {
          toastError(err);
          // Interceptor já remove o token e setIsAuth(false) em 401
        }
      }
    })();

    return () => {
      if (splashTimeoutRef.current) clearTimeout(splashTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;
      if (!Object.keys(socket).length) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }
      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === 'update' && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        io.off(`company-${user.companyId}-user`);
        // io.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [user]);

  /**
   * Fluxo pós-autenticação: usado por login e signup.
   * Recebe { token, refreshToken } e faz redirect para o app.
   */
  const handleAuthWithTokens = async (authData) => {
    if (splashTimeoutRef.current) {
      clearTimeout(splashTimeoutRef.current);
      splashTimeoutRef.current = null;
    }
    setLoading(true);

    const { token, refreshToken } = authData || {};
    if (!token) {
      setLoading(false);
      throw new Error('Resposta de autenticação inválida');
    }

    localStorage.setItem('token', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    api.defaults.headers.Authorization = `Bearer ${token}`;

    let user = null;
    try {
      const { data: meData } = await api.get('/auth/me');
      user = meData;
    } catch (_) {
      // /auth/me falhou; redirect e deixa o init carregar
    }

    const company = user?.company;
    if (company && has(company, 'companieSettings') && isArray(company.companieSettings?.[0])) {
      const setting = company.companieSettings[0].find((s) => s.key === 'campaignsEnabled');
      if (setting && setting.value === 'true') {
        localStorage.setItem('cshow', null);
      }
      const signSetting = company.companieSettings[0].find((s) => s.key === 'sendSignMessage');
      if (signSetting && signSetting.value === 'enabled') {
        localStorage.setItem('sendSignMessage', signSetting.value === 'enable');
      }
    }
    if (user?.profileImage) {
      localStorage.setItem('profileImage', user.profileImage);
    }

    if (user) setUser(user);
    setIsAuth(true);
    setLoginOrigin(localStorage.getItem('loginOrigin') || 'default');

    // Bloqueio e aviso de vencimento são responsabilidade do PlanPermissionsContext,
    // que lê user.trial e user.company.status vindos do /auth/me.
    // Aqui apenas redirecionamos para o app — o context decide se exibe bloqueio.
    window.location.href = '/tickets/conversation';
  };

  const handleLogin = async (userData) => {
    try {
      const { data } = await api.post('/auth/login', userData);
      await handleAuthWithTokens(data);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const origin = localStorage.getItem('loginOrigin') || 'default';

    const doRedirect = () => {
      setIsAuth(false);
      setUser({});
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('cshow');
      localStorage.removeItem('loginOrigin');
      api.defaults.headers.Authorization = undefined;
      setLoginOrigin('default');
      if (origin === 'mobile') {
        history.push('/mobile-login');
      } else {
        history.push('/login');
      }
    };

    // Captura o token antes de limpar — openApi não passa pelo interceptor de refresh,
    // então um 401 (token expirado) é simplesmente ignorado e o logout local prossegue
    const token = localStorage.getItem('token');
    if (token) {
      openApi
        .delete('/auth/logout', { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
    // Limpeza de push em segundo plano (não bloqueia)
    unsubscribeAndUnregister().catch(() => {});
    // Logout e redirect logo em seguida; usuário não fica esperando API/push
    doRedirect();
  };

  const handleSetLoginOrigin = (origin = 'default') => {
    localStorage.setItem('loginOrigin', origin);
    setLoginOrigin(origin);
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch (_) {
      return null;
    }
  };

  const refreshUser = async () => {
    const data = await getCurrentUserInfo();
    if (data) setUser(data);
    return data;
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleAuthWithTokens,
    handleLogout,
    handleSetLoginOrigin,
    isMobileSession: loginOrigin === 'mobile',
    getCurrentUserInfo,
    refreshUser,
    socket,
  };
};

export default useAuth;
